import {
  S3Client,
  CreateMultipartUploadCommand,
  UploadPartCommand,
  CompleteMultipartUploadCommand,
  AbortMultipartUploadCommand,
} from "@aws-sdk/client-s3";
import { v4 as uuidv4 } from "uuid";
import Mixpanel from "./mixpanel";
import { FetchHttpHandler } from "@smithy/fetch-http-handler";
import * as Sentry from "@sentry/react";
import crc32 from "crc-32";

const S3_BUCKET = process.env.REACT_APP_ASSET_S3_BUCKET!;
const S3_ENTERPRISE_BUCKET = process.env.REACT_APP_ENTERPRISE_ASSET_S3_BUCKET!;
const REGION = "us-west-2";
const PART_SIZE = 5 * 1024 * 1024; // 5MB

const base64EncodeCRC32 = (buffer: ArrayBuffer): string => {
  const crc = crc32.buf(new Uint8Array(buffer));
  const unsigned = crc >>> 0;
  const bytes = new Uint8Array(4);
  new DataView(bytes.buffer).setUint32(0, unsigned, false);
  return btoa(String.fromCharCode.apply(null, Array.from(bytes)));
};

export const uploadToS3 = async (
  userId: string,
  file: File,
  folder: string,
  contentType: string,
  awsAccessKeyId: string | undefined,
  awsSecretAccessKey: string | undefined,
  awsSessionToken: string | undefined,
  onProgress: (progress: number) => void,
  onSuccess: (key: string) => void,
  onFailure: (error: any) => void,
  isAddEnterprise: boolean = false
): Promise<void> => {
  if (!userId || !awsAccessKeyId || !awsSecretAccessKey || !awsSessionToken) {
    onFailure("Missing AWS credentials or user ID");
    return;
  }

  Mixpanel.track("s3_upload_start");

  const s3Client = new S3Client({
    region: REGION,
    credentials: {
      accessKeyId: awsAccessKeyId,
      secretAccessKey: awsSecretAccessKey,
      sessionToken: awsSessionToken,
    },
    requestHandler: new FetchHttpHandler({ requestTimeout: 60000 }),
    maxAttempts: 3,
  });

  const bucket = isAddEnterprise ? S3_ENTERPRISE_BUCKET : S3_BUCKET;
  const key = `${folder}/${uuidv4()}`;
  let uploadId = "";

  try {
    const createRes = await s3Client.send(
      new CreateMultipartUploadCommand({
        Bucket: bucket,
        Key: key,
        ContentDisposition: file.type.startsWith("video/")
          ? `inline; filename="${file.name}"`
          : `attachment; filename="${file.name}"`,
        ContentType: contentType,
      })
    );

    uploadId = createRes.UploadId!;
    const totalParts = Math.ceil(file.size / PART_SIZE);
    const uploadedParts = [];

    // Step 2: Upload each part
    for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
      const start = (partNumber - 1) * PART_SIZE;
      const end = Math.min(start + PART_SIZE, file.size);
      const blobPart = file.slice(start, end);
      const buffer = await blobPart.arrayBuffer();
      const checksum = base64EncodeCRC32(buffer);

      const uploadPartRes = await s3Client.send(
        new UploadPartCommand({
          Bucket: bucket,
          Key: key,
          UploadId: uploadId,
          PartNumber: partNumber,
          Body: blobPart,
          ChecksumCRC32: checksum, //
        })
      );

      uploadedParts.push({
        ETag: uploadPartRes.ETag!,
        PartNumber: partNumber,
      });

      onProgress(Math.round((partNumber / totalParts) * 100));
    }

    // Step 3: Complete multipart upload
    await s3Client.send(
      new CompleteMultipartUploadCommand({
        Bucket: bucket,
        Key: key,
        UploadId: uploadId,
        MultipartUpload: { Parts: uploadedParts },
      })
    );

    Mixpanel.track("s3_upload_success");
    onSuccess(key);
  } catch (error) {
    if (uploadId) {
      try {
        await s3Client.send(
          new AbortMultipartUploadCommand({
            Bucket: bucket,
            Key: key,
            UploadId: uploadId,
          })
        );
      } catch (abortError) {
        console.warn("Failed to abort multipart upload", abortError);
      }
    }

    console.error("S3 upload error", error);
    Sentry.captureException(error, {
      extra: {
        userId,
        fileName: file.name,
        folder,
        contentType,
      },
    });
    onFailure(error);
  }
};

export const awaitableUploadToS3 = (
  userId: string,
  file: File,
  folder: string,
  contentType: string,
  awsAccessKeyId: string | undefined,
  awsSecretAccessKey: string | undefined,
  awsSessionToken: string | undefined,
  onProgress: (progress: number) => void,
  isAddEnterprise: boolean = false
): Promise<string> => {
  return new Promise((resolve, reject) => {
    uploadToS3(
      userId,
      file,
      folder,
      contentType,
      awsAccessKeyId,
      awsSecretAccessKey,
      awsSessionToken,
      onProgress,
      (key) => resolve(key),
      (error) => reject(error),
      isAddEnterprise
    );
  });
};